{
  "en": {
    "ai": {
      "activation": {
        "activate": "Switch on",
        "activated": "The AI assistant is on for {client}.",
        "agreement_required": "Tick the box to agree first.",
        "deactivated": "The AI assistant is off for {client}.",
        "not_now": "Not now",
        "prompt": {
          "agreement": "On behalf of {client}, I agree to Google Cloud as a sub-processor for this feature.",
          "dpa_label": "Updated data processing agreement",
          "dpa_url": "https://ziggu.io/terms-and-conditions-13-10-2026",
          "intro": "Have conversations summarised and replies suggested. Free to try until 31 December 2026.",
          "processing": "To use the assistant, the text of conversations is processed through Google Cloud (Gemini) within the EU. Google does not use this data to train models.",
          "retention": "We keep assistant chats for {count} month. | We keep assistant chats for {count} months.",
          "settings": "You can switch the assistant on or off at any time in the settings, under {features} > {conversations}.",
          "title": "New: AI assistant for your project team"
        },
        "settings": {
          "description": "Summarises conversations and suggests replies. Switching it on asks you to agree to Google Cloud as a sub-processor.",
          "impersonation": "Only team members with the 'Owner' role can switch this on or off. This is not possible while impersonating.",
          "owner_only": "Ask an owner to switch on the AI assistant.",
          "owner_only_enabled": "Only an owner can switch off the AI assistant.",
          "title": "AI assistant",
          "toggle": "AI assistant in conversations"
        },
        "snoozed": "You can switch the AI assistant on later in the settings, under {features} > {conversations}."
      },
      "assistant": {
        "all_projects": "All projects",
        "cap": {
          "action": "Start a new conversation",
          "message": "You've reached the conversation limit."
        },
        "empty_state": {
          "subtitle": "Ask me anything about your clients",
          "title": "How can I help you?"
        },
        "errors": {
          "budget_reached": "This month's AI budget has been used. Contact Ziggu to raise it.",
          "generic": "Something went wrong. Please try again."
        },
        "history": {
          "empty": "No past conversations yet",
          "title": "Past conversations"
        },
        "input": {
          "disclaimer": "Results may be inaccurate. Always verify important information.",
          "hint": "Enter to send · Shift+Enter for new line",
          "placeholder": "Ask about your clients..."
        },
        "name": "Ziggy",
        "suggestions": {
          "help_notifications": "How do I set up notifications?",
          "help_project_settings": "Where do I find project settings?",
          "open_decisions": "Which decisions are still open?",
          "unassigned_units": "Which units have no customer yet?"
        },
        "support_cta": {
          "action": "Contact support",
          "prompt": "Not the answer you were looking for?"
        }
      },
      "dashboard": {
        "agents": {
          "all": "All modules",
          "assistant": "Assistant",
          "conversation_agent": "Conversation agent",
          "label": "Module",
          "translation": "Translation"
        },
        "allowance": {
          "edit": "Edit allowance"
        },
        "allowance_boost": {
          "action": "Boost this period",
          "action_short": "Boost",
          "amount_label": "Extra amount",
          "existing": "A boost of {amount} is already running this period. This amount is added to it.",
          "expires": "Added on top of the allowance for this period only, and it expires on {date}.",
          "expires_next_reset": "Added on top of the allowance for this period only, and it expires at the next reset.",
          "save": "Add boost",
          "saved": "Boost of {amount} added"
        },
        "allowance_history": {
          "empty": "No changes yet",
          "empty_hint": "This client still runs on the allowance it was given.",
          "entry": "{field}: {before} → {after}",
          "package_amount": "package amount",
          "title": "Allowance history",
          "unknown_author": "Unknown"
        },
        "chat_detail": {
          "employee": "Employee",
          "empty": "This chat has no messages",
          "feedback": {
            "down": "Bad answer",
            "up": "Good answer"
          },
          "text_hidden": "Not shared by the employee",
          "title": "Conversation"
        },
        "chats": {
          "columns": {
            "client": "Client",
            "cost": "Cost",
            "employee": "Employee",
            "intent": "Intent",
            "ticket": "Ticket",
            "turns": "Turns",
            "when": "When"
          },
          "empty": "No chats in this range",
          "feedback_label": "Rating",
          "feedbacks": {
            "all": "Any rating",
            "down": "Thumbs down",
            "up": "Thumbs up"
          },
          "intent_label": "Intent",
          "open": "Open chat",
          "title": "Chats",
          "via": "via {name}"
        },
        "client": {
          "allowance_boost": "{amount} boost until {date}",
          "allowance_boost_next_reset": "{amount} boost until the next reset",
          "allowance_free": "{amount} free",
          "allowance_paid": "{amount} paid",
          "employees_active": "{count} employee active | {count} employees active",
          "over_since": "Over since {date}",
          "title": "{name} · AI",
          "used": "{used} of {allowance} used"
        },
        "clients": {
          "allowance_split": "Free {free} · Paid {paid}",
          "allowance_split_boost": "Free {free} · Paid {paid} · Boost {boost}",
          "columns": {
            "actions": "Actions",
            "allowance": "Allowance",
            "chats": "Chats in range",
            "client": "Client",
            "spend": "Spend in range",
            "status": "Status",
            "used": "Used this month"
          },
          "empty": "No client used AI in this range",
          "export": "Export",
          "title": "Clients"
        },
        "empty_hint": "Nothing matched this module and date range.",
        "intent_mix": {
          "empty": "No chats in this range",
          "row": "{chats} chat · {amount} | {chats} chats · {amount}",
          "title": "Intent mix"
        },
        "intents": {
          "all": "Any intent",
          "draft": "Draft a reply",
          "none": "No intent",
          "question": "Ask",
          "summary": "Summary"
        },
        "kpi": {
          "clients_active": "Active clients",
          "employees_active": "Active employees",
          "spend_month": "Spend this month",
          "spend_month_detail": "Calendar month to date",
          "spend_range": "Spend in range",
          "spend_range_detail": "{count} chat in this range | {count} chats in this range",
          "spend_today": "Spend today",
          "tickets_assisted": "Tickets assisted"
        },
        "load_failed": {
          "description": "Reload the page or try another range.",
          "title": "Couldn't load the AI usage"
        },
        "range": {
          "label": "Date range"
        },
        "spend": {
          "empty": "No spend in this range",
          "monthly_empty": "No spend in the last {months} months",
          "monthly_hint": "Last {months} months · not affected by the date range",
          "monthly_title": "Cost per month",
          "monthly_total": "{amount} in the last {months} months · not affected by the date range",
          "title": "Spend per day",
          "total": "{amount} in range",
          "view": {
            "label": "Show spend",
            "per_day": "Per day",
            "running_total": "Running total"
          }
        },
        "status": {
          "over": "Over budget",
          "over_days": "Over · 1 day | Over · {count} days"
        },
        "title": "AI usage"
      },
      "settings": {
        "budget": {
          "amount_error": "Enter an amount like 2 or 2.50",
          "boost_label": "Temporary boost",
          "boost_label_until": "Temporary boost until {date}",
          "description": "Monthly cost cap for AI usage (test phase). Recalculated based on token usage across all conversations this month.",
          "free_label": "Free allowance",
          "override_help": "Leave empty to use the package amount ({amount}).",
          "override_label": "Free allowance override",
          "paid_label": "Paid allowance",
          "save": "Save",
          "title": "Monthly budget",
          "total_label": "Total allowance",
          "usage_label": "Used this month",
          "usage_of": "Used this month, of {amount}",
          "use_package_amount": "Use package amount"
        },
        "description": "Configuration for AI features in your organisation.",
        "title": "AI settings",
        "usage": {
          "description": "How much of this month's AI allowance is used. At 100% the AI features stop until the allowance resets next month.",
          "title": "AI usage"
        }
      },
      "ticket_assistant": {
        "actions": {
          "adjust": "Adjust",
          "ask": "Ask",
          "draft": "Draft a reply",
          "draft_empty": "No messages to reply to yet",
          "follow_up": "Follow up",
          "follow_up_stop": "Stop following up",
          "new": "New question",
          "summary": "Summarise conversation",
          "summary_empty": "No messages to summarise yet",
          "use_as_reply": "Use as reply"
        },
        "adjusting_draft": "Adjusting this draft",
        "beta": "Beta",
        "beta_tooltip": "Free until the end of 2026. After that, usage will be limited.",
        "budget": {
          "description": "This month's AI budget has been used. Your earlier answers stay available.",
          "request": "We have used this month's AI budget and would like to raise it.",
          "request_low": "We have almost used this month's AI budget and would like to raise it.",
          "title": "AI budget reached",
          "warn": "{percent}% of this month's AI budget used."
        },
        "cap": "This chat has grown long. Ask a new question instead.",
        "collapse": "Hide assistant",
        "conversation": "Conversation",
        "digest": {
          "decisions_open": "no open decisions | 1 open decision | {count} open decisions",
          "installments_unpaid": "no unpaid installments | 1 unpaid installment | {count} unpaid installments",
          "issues_open": "no open aftercare issues | 1 open aftercare issue | {count} open aftercare issues",
          "nothing_open": "Nothing open on this unit",
          "overdue": "{count} overdue",
          "title": "At a glance"
        },
        "earlier_answers": "Earlier answers",
        "empty": "Ask about this conversation, or start with one of these.",
        "expand": "Show assistant",
        "feedback": {
          "bad": "Bad answer",
          "consent": "Ziggu will see this conversation. It can include customer names and details.",
          "consent_shared": "This conversation is already shared with Ziggu.",
          "good": "Good answer",
          "note": "What should it have said? (optional)",
          "send": "Send",
          "share": "Share with Ziggu",
          "stop_sharing": "Stop sharing",
          "thanks": "Thanks for your feedback.",
          "thanks_shared": "Thanks. Shared with Ziggu.",
          "undo": "Undo"
        },
        "following_up": "Following up this answer",
        "footer": "Ziggu support only reads this if you share it or allow Ziggu access. Answers are made by AI and can contain errors.",
        "footer_shared": "You shared this conversation with Ziggu. Answers are made by AI and can contain errors.",
        "input": {
          "adjust_draft": "Adjust this draft, e.g. shorter or more formal",
          "follow_up": "Ask a follow-up",
          "label": "Your question",
          "placeholder": "Ask about this conversation"
        },
        "links": {
          "aftercare": "Aftercare",
          "decisions": "Decisions",
          "financials": "Financials"
        },
        "placeholders": {
          "confirm": {
            "accept": "Send anyway",
            "header": "Send with placeholders?",
            "message": "The assistant left 1 placeholder in this reply that you have not filled in yet. | The assistant left {count} placeholders in this reply that you have not filled in yet."
          },
          "remaining": "1 placeholder to fill in | {count} placeholders to fill in"
        },
        "replace_reply": {
          "confirm": {
            "accept": "Replace",
            "header": "Replace what you typed?",
            "message": "The draft replaces the text in the reply box."
          }
        },
        "resize": "Resize assistant",
        "suggestions": {
          "history": "What is the history with this customer?",
          "issues_open": "How many aftercare issues are still open?",
          "undecided": "What has this customer not decided yet?"
        },
        "thinking": "The assistant is working...",
        "title": "Assistant"
      }
    }
  }
}
